import {
  SearchActivityRuns,
  SearchLastActivityRunsByRuntime,
} from '@robocorp/robocloud-search-interface';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { z } from 'zod';
import { Runtime } from '@robocorp/robocloud-internal-cds-sdk';
import { asyncForLoop } from '@robocorp/robocloud-shared-utils';

import { OnDemandWorkerInput } from '~/schemas/workersSchemas';
import { getActivityRuns, getLastActivityRuns } from '~/services/searchAPI';
import { runtimeService } from '~/services/workspaceAPI';
import { WithWorkspaceId } from '~/types/common';
import { WorkerUpdate } from '~/types/resources';
import {
  ApiQueryFnData,
  formatErrorPayload,
  QueryError,
  UseApiInfiniteQueryOptions,
  UseApiMutationOptions,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';
import { useLazyQuery } from '../useLazyQuery';

const ROOT_KEY = 'runtimes';
const ROOT_ON_DEMAND_KEY = 'runtimes_ondemand';
const ROOT_RUNTIMES_LAST_RUNS_KEY = 'runtimes_last_runs';
const ROOT_RUNTIMES_ACTIVITY_RUNS_KEY = 'runtimes_activity_runs';
export const getWorkersQueryKey = (workspaceId: string) => [ROOT_KEY, workspaceId];
export const getRuntimeQueryKey = (runtimeId: string) => [ROOT_KEY, runtimeId];
export const getWorkersOnDemandQueryKey = (workspaceId: string) => [
  ROOT_ON_DEMAND_KEY,
  workspaceId,
];
export const getRuntimesLastActivityRunsQueryKey = (
  workspaceId: string,
  options?: Record<string, unknown>,
) => [ROOT_RUNTIMES_LAST_RUNS_KEY, workspaceId, options];
export const getRuntimesActivityRunsQueryKey = (
  workspaceId: string,
  options?: Record<string, unknown>,
) => [ROOT_RUNTIMES_ACTIVITY_RUNS_KEY, workspaceId, options];

export enum RuntimesQueryErrors {
  GET_RUNTIME_ERROR = 'GET_SINGLE_RUNTIME_ERROR',
  GET_RUNTIMES_ERROR = 'GET_RUNTIMES_ERROR',
  GET_RUNTIMES_ON_DEMAND_ENVIRONMENTS_ERROR = 'GET_RUNTIMES_ON_DEMAND_ENVIRONMENTS_ERROR',
  GET_ACTIVITY_RUNS_ERROR = 'GET_ACTIVITY_RUNS_ERROR',
  GET_LAST_ACTIVITY_RUNS_ERROR = 'GET_LAST_ACTIVITY_RUNS_ERROR',
  CREATE_RUNTIME_LINK_ERROR = 'CREATE_RUNTIME_LINK_ERROR',
  CREATE_RUNTIME_GROUP_ERROR = 'CREATE_RUNTIME_GROUP_ERROR',
  CREATE_RUNTIME_ON_DEMAND_ENVIRONMENT_ERROR = 'CREATE_RUNTIME_ON_DEMAND_ENVIRONMENT_ERROR',
  EDIT_RUNTIME_ERROR = 'EDIT_RUNTIME_ERROR',
  DELETE_RUNTIME_ERROR = 'CREATE_RUNTIME_GROUP_ERROR',
}

export const useWorkersQuery = (
  workspaceId: string,
  options?: UseApiQueryOptions<typeof runtimeService.getAll>,
) => {
  const dispatch = useDispatch();
  return useQuery(
    getWorkersQueryKey(workspaceId),
    async () => validateApiResponse(await runtimeService.getAll({ workspaceId })).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.GET_RUNTIMES_ERROR }
          ),
        );
      },
      refetchOnMount: true,
      ...options,
    },
  );
};

export const fetchWorkerQuery = (args: { workerId: string } & WithWorkspaceId) => async () => {
  const response = validateApiResponse(
    await runtimeService.getOneById({ workspaceId: args.workspaceId, id: args.workerId }),
  );
  return response?.data;
};

export const useWorkerQuery = (
  workerId: string,
  options?: UseApiQueryOptions<typeof runtimeService.getOneById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(getRuntimeQueryKey(workerId), fetchWorkerQuery({ workspaceId, workerId }), {
    onError: (error) => {
      dispatch(
        formatErrorPayload(error, {
          // type: RuntimesQueryErrors.GET_RUNTIME_ERROR
          workerId,
        }),
      );
    },
    ...options,
  });
};

export const useWorkerLazyQuery = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useLazyQuery<{ workerId: string }, Runtime | undefined, QueryError>(
    ({ workerId }) => getRuntimeQueryKey(workerId),
    (args) => fetchWorkerQuery({ workspaceId, ...args }),
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(error, {
            // type: RuntimesQueryErrors.GET_RUNTIME_ERROR
          }),
        );
      },
    },
  );
};

export const useOnDemandWorkersQuery = (
  workspaceId: string,
  options?: UseApiQueryOptions<typeof runtimeService.listOnDemandRuntimes>,
) => {
  const dispatch = useDispatch();
  return useQuery(
    getWorkersOnDemandQueryKey(workspaceId),
    async () =>
      validateApiResponse(await runtimeService.listOnDemandRuntimes({ workspaceId })).data ?? [],
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.GET_RUNTIMES_ON_DEMAND_ENVIRONMENTS_ERROR }
          ),
        );
      },
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useOnDemandWorkerQuery = (
  workerId: string,
  options?: UseApiQueryOptions<typeof runtimeService.getOnDemandRuntimeById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getRuntimeQueryKey(workerId),
    async () =>
      validateApiResponse(
        await runtimeService.getOnDemandRuntimeById({ workspaceId, onDemandRuntimeId: workerId }),
      ).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(error, {
            // type: RuntimesQueryErrors.GET_RUNTIME_ERROR
            workerId,
          }),
        );
      },
      ...options,
    },
  );
};

export const useWorkersLastActivityRunsQuery = (
  workspaceId: string,
  queryOptions: Partial<
    z.infer<(typeof SearchLastActivityRunsByRuntime)['request']['queryString']>
  >,
  options?: UseApiQueryOptions<typeof getLastActivityRuns>,
) => {
  const dispatch = useDispatch();
  return useQuery(
    getRuntimesLastActivityRunsQueryKey(workspaceId, queryOptions),
    async () =>
      validateApiResponse(await getLastActivityRuns({ workspaceId, ...queryOptions })).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.GET_LAST_ACTIVITY_RUNS_ERROR }
          ),
        );
      },
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useWorkersActivityRunsQuery = <T extends typeof getActivityRuns>(
  workspaceId: string,
  queryOptions: Partial<z.infer<(typeof SearchActivityRuns)['request']['queryString']>>,
  options?: UseApiQueryOptions<T, ApiQueryFnData<T>['data']>,
) => {
  const dispatch = useDispatch();
  return useQuery(
    getRuntimesActivityRunsQueryKey(workspaceId, queryOptions),
    async () =>
      validateApiResponse(await getActivityRuns({ workspaceId, ...queryOptions })).data?.data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.GET_ACTIVITY_RUNS_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useWorkersStepRunsInfiniteQuery = (
  workspaceId: string,
  queryOptions: Partial<z.infer<(typeof SearchActivityRuns)['request']['queryString']>>,
  options?: UseApiInfiniteQueryOptions<typeof getActivityRuns>,
) => {
  const dispatch = useDispatch();
  return useInfiniteQuery(
    getRuntimesActivityRunsQueryKey(workspaceId, queryOptions),
    async ({ pageParam }) =>
      validateApiResponse(await getActivityRuns({ workspaceId, ...queryOptions, ...pageParam }))
        .data,
    {
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
      getPreviousPageParam: (firstPage) => firstPage?.previousCursor,
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.GET_ACTIVITY_RUNS_ERROR }
          ),
        );
      },
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useLinkWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.linkRuntime>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async () => {
      const response = validateApiResponse(await runtimeService.linkRuntime({ workspaceId }));
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.CREATE_RUNTIME_LINK_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useCreateOnDemandWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.createOnDemandRuntime>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (worker: OnDemandWorkerInput) => {
      const response = validateApiResponse(
        await runtimeService.createOnDemandRuntime({
          workspaceId,
          worker,
        }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.CREATE_RUNTIME_ON_DEMAND_ENVIRONMENT_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useEditOnDemandWorkerMutation = <
  T extends typeof runtimeService.editOnDemandRuntimeById,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId, worker }: { workerId: string; worker: ApiQueryFnData<T> }) =>
      validateApiResponse(
        await runtimeService.editOnDemandRuntimeById({
          workspaceId,
          onDemandRuntimeId: workerId,
          onDemandRuntime: worker,
        }),
      ).data,
    {
      onSuccess: (_, { workerId, worker }) => {
        queryClient.setQueryData(getRuntimeQueryKey(workerId), worker);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.EDIT_RUNTIME_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useDeleteOnDemandWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteOnDemandRuntimeById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId }: { workerId: string }) =>
      validateApiResponse(
        await runtimeService.deleteOnDemandRuntimeById({ workspaceId, id: workerId }),
      ).data,
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.DELETE_RUNTIME_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useBulkDeleteOnDemandWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteOnDemandRuntimeById>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (onDemandIds: string[]) => {
      const responses: Awaited<ReturnType<typeof runtimeService.deleteOnDemandRuntimeById>>[] = [];
      await asyncForLoop(onDemandIds, async (onDemandId) => {
        responses.push(
          await runtimeService.deleteOnDemandRuntimeById({ workspaceId, id: onDemandId }),
        );
      });

      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkersOnDemandQueryKey(workspaceId));
      },
      ...options,
    },
  );
};

export const useDeleteWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteOneById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId }: { workerId: string }) =>
      validateApiResponse(await runtimeService.deleteOneById({ workspaceId, id: workerId })).data,
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.DELETE_RUNTIME_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useBulkDeleteWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteOneById>,
) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (workerIds: string[]) => {
      const responses: Awaited<ReturnType<typeof runtimeService.deleteOneById>>[] = [];
      await asyncForLoop(workerIds, async (workerId) => {
        responses.push(await runtimeService.deleteOneById({ workspaceId, id: workerId }));
      });

      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkersQueryKey(workspaceId));
      },
      ...options,
    },
  );
};

export const useEditWorkerMutation = <T extends typeof runtimeService.editOneById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId, worker }: { workerId: string; worker: ApiQueryFnData<T> }) =>
      validateApiResponse(
        await runtimeService.editOneById({ workspaceId, runtimeId: workerId, runtime: worker }),
      ).data,
    {
      onSuccess: (_, { workerId, worker }) => {
        queryClient.setQueryData(getRuntimeQueryKey(workerId), worker);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.EDIT_RUNTIME_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useUpdateWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.updateWorker>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (update: WorkerUpdate) => {
      const response = validateApiResponse(
        await runtimeService.updateWorker({ workspaceId }, update),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(getRuntimeQueryKey(request.workerId));
      },
      ...options,
    },
  );
};

export const useBulkUpdateWorkerMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.updateWorker>,
) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (updates: WorkerUpdate[]) => {
      const responses: Awaited<ReturnType<typeof runtimeService.updateWorker>>[] = [];
      await asyncForLoop(updates, async (update) => {
        responses.push(await runtimeService.updateWorker({ workspaceId }, update));
      });

      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkersQueryKey(workspaceId));
      },
      ...options,
    },
  );
};
